import React from "react";
import useRoles from "../../hooks/useRoles";
import Loader from "../../components/common/GlobalLoader";

const UserAdministration = () => {
  const {
    roles,
    loading: rolesLoading,
    saving: rolesSaving,
    error: rolesError,
    permissionsCatalog,
    createRole,
    updateRole,
    deleteRole,
  } = useRoles();

  const [showAddRoleModal, setShowAddRoleModal] = React.useState(false);
  const [newRoleName, setNewRoleName] = React.useState("");
  const [newRoleDescription, setNewRoleDescription] = React.useState("");
  const [newRolePermissions, setNewRolePermissions] = React.useState({});

  const [selectedRoleId, setSelectedRoleId] = React.useState("");
  const [selectedRolePermissions, setSelectedRolePermissions] = React.useState({});
  const [roleSelectOpen, setRoleSelectOpen] = React.useState(false);

  const isBusy = rolesLoading || rolesSaving;

  const selectedRole = roles.find((r) => r._id === selectedRoleId);

  // Initialize permissions when role is selected
  React.useEffect(() => {
    if (selectedRole) {
      setSelectedRolePermissions(selectedRole.permissions || {});
    }
  }, [selectedRole]);

  // Initialize new role permissions
  React.useEffect(() => {
    const initialPermissions = permissionsCatalog.reduce(
      (acc, p) => ({ ...acc, [p.key]: false }),
      {}
    );
    setNewRolePermissions(initialPermissions);
  }, [permissionsCatalog]);

  // Group permissions for better organization
  const actionPermissions = React.useMemo(
    () =>
      permissionsCatalog.filter(
        (p) => p.key === "canCreateSalesOrders" || p.key === "canViewPrices"
      ),
    [permissionsCatalog]
  );
  const modulePermissions = React.useMemo(
    () =>
      permissionsCatalog.filter(
        (p) => p.key !== "canCreateSalesOrders" && p.key !== "canViewPrices"
      ),
    [permissionsCatalog]
  );

  const handleNewRolePermissionToggle = (permissionKey) => {
    setNewRolePermissions((prev) => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
    }));
  };

  const handleSelectedRolePermissionToggle = (permissionKey) => {
    setSelectedRolePermissions((prev) => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
    }));
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      alert("Please enter a role name.");
      return;
    }

    const payload = {
      name: newRoleName.trim(),
      description: newRoleDescription.trim() || undefined,
      permissions: newRolePermissions,
    };

    const created = await createRole(payload);
    if (created) {
      setNewRoleName("");
      setNewRoleDescription("");
      setNewRolePermissions(
        permissionsCatalog.reduce(
          (acc, p) => ({ ...acc, [p.key]: false }),
          {}
        )
      );
      setShowAddRoleModal(false);
    }
  };

  const handleUpdateRolePermissions = async () => {
    if (!selectedRoleId) {
      alert("Please select a role first.");
      return;
    }

    await updateRole(selectedRoleId, {
      permissions: selectedRolePermissions,
    });
  };

  const handleSelectRole = (roleId) => {
    setSelectedRoleId(roleId);
    const role = roles.find((r) => r._id === roleId);
    if (role) {
      setSelectedRolePermissions(role.permissions || {});
    }
  };

  React.useEffect(() => {
    if (!roleSelectOpen) return;
    const onDocPointerDown = (e) => {
      const root = document.querySelector("[data-admin-role-picker]");
      if (root && !root.contains(e.target)) {
        setRoleSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
    };
  }, [roleSelectOpen]);

  React.useEffect(() => {
    if (showAddRoleModal) setRoleSelectOpen(false);
  }, [showAddRoleModal]);

  const handleDeleteRole = async () => {
    if (!selectedRoleId) {
      alert("Please select a role first.");
      return;
    }

    const role = roles.find((r) => r._id === selectedRoleId);
    if (!role) return;

    // Only Super Admin cannot be deleted
    if (role.name === "Super Admin") {
      alert("Super Admin role cannot be deleted.");
      return;
    }

    const confirmed = window.confirm(
      `Delete role "${role.name}"? This cannot be undone.`
    );
    if (!confirmed) return;

    await deleteRole(role._id);
    setSelectedRoleId("");
    setSelectedRolePermissions({});
  };

  return (
    <div className="relative">
      {isBusy && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg z-40">
          <Loader />
        </div>
      )}

      <div
        className={`bg-white border rounded-xl p-4 sm:p-6 shadow-md max-w-7xl mx-auto ${
          isBusy ? "opacity-40 pointer-events-none select-none" : ""
        }`}
      >
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold theme-text-primary">
              Roles &amp; Permissions
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Define reusable roles for your organisation and control which
              actions each role can perform.
            </p>
          </div>
        </div>

        {rolesError && (
          <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
            {rolesError}
          </div>
        )}


        {/* Roles & Permissions Management */}
        <section className="mb-8 border rounded-lg p-4 sm:p-5 bg-gray-50">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Manage Roles &amp; Permissions
            </h3>
            <button
              onClick={() => setShowAddRoleModal(true)}
              className="inline-flex items-center justify-center rounded-md theme-bg-primary px-4 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-1 w-full sm:w-auto"
            >
              + Add Role
            </button>
          </div>

          {/* Select Role (custom list — native select mis-sizes / mis-anchors on mobile) */}
          <div className={`mb-4 ${roleSelectOpen ? "relative z-40" : ""}`}>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Select Role to Update Permissions
            </label>
            <div data-admin-role-picker className="relative">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => setRoleSelectOpen((o) => !o)}
                className="w-full min-h-[44px] flex items-center justify-between gap-2 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
                aria-expanded={roleSelectOpen}
                aria-haspopup="listbox"
              >
                <span className="truncate">
                  {selectedRoleId
                    ? (() => {
                        const r = roles.find((x) => x._id === selectedRoleId);
                        return r
                          ? `${r.name}${r.isSystem ? " (Default)" : ""}`
                          : "Select a role...";
                      })()
                    : "Select a role..."}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                    roleSelectOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {roleSelectOpen && (
                <ul
                  className="absolute left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg z-50"
                  role="listbox"
                >
                  <li>
                    <button
                      type="button"
                      role="option"
                      className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 ${
                        !selectedRoleId
                          ? "bg-indigo-50 font-medium text-gray-900"
                          : "text-gray-800"
                      }`}
                      onClick={() => {
                        handleSelectRole("");
                        setRoleSelectOpen(false);
                      }}
                    >
                      Select a role...
                    </button>
                  </li>
                  {roles.map((role) => (
                    <li key={role._id}>
                      <button
                        type="button"
                        role="option"
                        className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 ${
                          selectedRoleId === role._id
                            ? "bg-indigo-50 font-medium text-gray-900"
                            : "text-gray-800"
                        }`}
                        onClick={() => {
                          handleSelectRole(role._id);
                          setRoleSelectOpen(false);
                        }}
                      >
                        {role.name}
                        {role.isSystem ? " (Default)" : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {selectedRole && selectedRole.description && (
              <p className="mt-2 text-xs text-gray-600">
                {selectedRole.description}
              </p>
            )}
          </div>

          {/* Update Permissions for Selected Role */}
          {selectedRole && (
            <div className="mt-4 p-4 bg-white border rounded-lg">
              <h4 className="text-sm font-medium text-gray-800 mb-3">
                Update Permissions for "{selectedRole.name}"
              </h4>
              
              {/* Action Permissions */}
              <div className="mb-4">
                <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Action Permissions
                </h5>
                <div className="space-y-2">
                  {actionPermissions.map((perm) => (
                    <label
                      key={perm.key}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedRolePermissions[perm.key]}
                        onChange={() =>
                          handleSelectedRolePermissionToggle(perm.key)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Module Access Permissions */}
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Module Access Permissions
                </h5>
                <div className="space-y-2">
                  {modulePermissions.map((perm) => (
                    <label
                      key={perm.key}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedRolePermissions[perm.key]}
                        onChange={() =>
                          handleSelectedRolePermissionToggle(perm.key)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3 mt-4">
                <button
                  onClick={handleUpdateRolePermissions}
                  className="inline-flex items-center justify-center rounded-md theme-bg-primary px-4 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-1 w-full sm:w-auto"
                >
                  Update Permissions
                </button>
                {selectedRole.name !== "Super Admin" && (
                  <button
                    onClick={handleDeleteRole}
                    className="inline-flex items-center justify-center rounded-md bg-red-600 text-white px-4 py-2.5 text-sm font-semibold shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 w-full sm:w-auto"
                  >
                    Delete Role
                  </button>
                )}
              </div>
            </div>
          )}

          {!selectedRoleId && roles.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              Select a role from the dropdown above to view and update its
              permissions.
            </p>
          )}
        </section>

        {/* Add Role Modal */}
        {showAddRoleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold theme-text-primary">
                  Create New Role
                </h3>
                <button
                  onClick={() => {
                    setShowAddRoleModal(false);
                    setNewRoleName("");
                    setNewRoleDescription("");
                    setNewRolePermissions(
                      permissionsCatalog.reduce(
                        (acc, p) => ({ ...acc, [p.key]: false }),
                        {}
                      )
                    );
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateRole} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="e.g. Price Only, Order Approver"
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    placeholder="Short explanation of what this role can do"
                    rows={3}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  
                  {/* Action Permissions */}
                  <div className="mb-4">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Action Permissions
                    </h5>
                    <div className="space-y-2">
                      {actionPermissions.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={!!newRolePermissions[perm.key]}
                            onChange={() =>
                              handleNewRolePermissionToggle(perm.key)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Module Access Permissions */}
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Module Access Permissions
                    </h5>
                    <div className="space-y-2">
                      {modulePermissions.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={!!newRolePermissions[perm.key]}
                            onChange={() =>
                              handleNewRolePermissionToggle(perm.key)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddRoleModal(false);
                      setNewRoleName("");
                      setNewRoleDescription("");
                      setNewRolePermissions(
                        permissionsCatalog.reduce(
                          (acc, p) => ({ ...acc, [p.key]: false }),
                          {}
                        )
                      );
                    }}
                    className="px-4 py-2.5 border rounded-md text-sm hover:bg-gray-50 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-md theme-bg-primary px-4 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-1 w-full sm:w-auto"
                  >
                    Create Role
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default UserAdministration;

